import React, { useState } from "react";
import {
  Card, CardBody, CardHeader, Nav, NavItem, NavLink, Table,
} from "reactstrap";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, Tooltip, Legend, Plugin,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useHorizonStrengthEval, StrengthBin, StrengthWindow } from "../hooks/useHorizonStrengthEval";
import { useTheme } from "../context/ThemeContext";
import { WINDOW_LABELS } from "../utils/windows";
import AssetClassToggle from "./AssetClassToggle";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Dashed coin-flip (50%) baseline so a bar above the line reads as "better than chance".
const baselinePlugin: Plugin<"bar"> = {
  id: "coinflip-baseline",
  afterDatasetsDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    const y = scales.y.getPixelForValue(50);
    if (y < chartArea.top || y > chartArea.bottom) return;
    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([5, 4]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(130,130,130,0.7)";
    ctx.moveTo(chartArea.left, y);
    ctx.lineTo(chartArea.right, y);
    ctx.stroke();
    ctx.restore();
  },
};

function winRateColor(wr: number, alpha = 0.7): string {
  if (wr >= 55) return `rgba(5,150,105,${alpha})`;      // green
  if (wr >= 50) return `rgba(132,204,22,${alpha})`;     // lime
  if (wr >= 45) return `rgba(202,138,4,${alpha})`;      // amber
  return `rgba(220,38,38,${alpha})`;                    // red
}

/** Win rate at the window's committed (headline) horizon for a strength bin, or null. */
function headlineWin(bin: StrengthBin, headline: number): StrengthCurvePick {
  const e = bin.curve.find(c => c.horizon === headline) ?? bin.best_edge ?? null;
  return e ? { win_rate: e.win_rate, count: e.count, avg_return: e.avg_return, horizon: e.horizon } : null;
}
type StrengthCurvePick = { win_rate: number; count: number; avg_return: number; horizon: number } | null;

/**
 * EdgeByConviction — visualizes the strength-stratified horizon eval: does the
 * historical win rate rise as the matched analogs agree more strongly on direction?
 * Sourced from /api/horizon-strength-eval (all matched signals, binned by
 * called-direction fraction). Complements the top-20 Signal Track Record above it.
 */
export default function EdgeByConviction(
  { className = "mt-4", defaultAssetClass = "all" }: { className?: string; defaultAssetClass?: string },
) {
  const data = useHorizonStrengthEval();
  const [activeTab, setActiveTab] = useState<string>("65");
  const [assetClass, setAssetClass] = useState<string>(defaultAssetClass);
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const textMutedColor = isDark ? "#94A3B8" : "#64748B";
  const gridColor = isDark ? "rgba(148, 163, 184, 0.2)" : "rgba(203, 213, 225, 0.5)";

  // Hide entirely until there's at least one window with data (avoids an empty card
  // before the archive has been scored).
  // windows is typed non-null, but the hook can hold a partial body — guard the access.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const hasData = data && Object.keys(data.windows ?? {}).length > 0;
  if (!hasData) return null;

  // Record<> types the lookup as always-defined, but the API may omit a window
  // (e.g. one with no archive yet), so guard for real at runtime.
  const win: StrengthWindow | undefined = data.windows[activeTab];
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!win) return null;

  // Asset classes present anywhere in the payload (drives the toggle).
  const available = new Set<string>();
  Object.values(data.windows).forEach((w) =>
    w.by_asset_class && Object.keys(w.by_asset_class).forEach((k) => available.add(k)),
  );

  // Fall back to "all" if the requested class isn't in this payload (e.g. a
  // defaultAssetClass="equity" against pre-stratification data) — avoids an empty view.
  const shownClass = assetClass === "all" || available.has(assetClass) ? assetClass : "all";

  const headline = win.headline_horizon;
  const bins = shownClass === "all" ? win.bins : (win.by_asset_class?.[shownClass]?.bins ?? []);
  const rows = bins.map(b => ({ bin: b, hl: headlineWin(b, headline) }));

  const chartData = {
    labels: rows.map(r => `${r.bin.label}%`),
    datasets: [{
      label: `Win rate @ ${headline}d`,
      data: rows.map(r => (r.hl ? r.hl.win_rate : null)),
      backgroundColor: rows.map(r => (r.hl ? winRateColor(r.hl.win_rate) : "rgba(130,130,130,0.3)")),
      borderRadius: 4,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items: { label: string }[]) => `Conviction ${items[0].label} of analogs`,
          label: (item: { dataIndex: number }) => {
            const r = rows[item.dataIndex];
            if (!r.hl) return "insufficient sample";
            return [
              `Win rate: ${r.hl.win_rate.toFixed(1)}%`,
              `Avg return: ${r.hl.avg_return >= 0 ? "+" : ""}${r.hl.avg_return.toFixed(2)}%`,
              `n = ${r.hl.count.toLocaleString()}`,
            ];
          },
        },
      },
    },
    scales: {
      x: { ticks: { color: textMutedColor }, grid: { display: false } },
      y: {
        ticks: { color: textMutedColor, callback: (v: string | number) => `${v}%` },
        grid: { color: gridColor },
        suggestedMin: 40,
        suggestedMax: 75,
      },
    },
  };

  return (
    <Card className={className}>
      <CardHeader style={{ borderBottom: "1px solid var(--border-color)", background: "var(--card-background)" }}>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h5 className="mb-0" style={{ fontWeight: 700 }}>Does edge rise with conviction?</h5>
          <div className="d-flex align-items-center flex-wrap gap-2">
            <AssetClassToggle value={shownClass} onChange={setAssetClass} available={available} />
            <Nav tabs style={{ borderBottom: "none" }}>
              {Object.entries(WINDOW_LABELS).map(([key, label]) => (
                <NavItem key={key}>
                  <NavLink
                    active={activeTab === key}
                    onClick={() => setActiveTab(key)}
                    style={{ cursor: "pointer", padding: "2px 10px", fontSize: 13, color: activeTab === key ? "var(--primary)" : "var(--text-muted)" }}
                  >
                    {label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <p className="text-muted mb-3" style={{ fontSize: 13.5 }}>
          Every matched signal — not just the featured top picks — grouped by how strongly its historical
          analogs agreed on direction (e.g. <strong>80-90%</strong> = 80-90% of the matched patterns moved
          the called way next). Bars show the realized win rate at the committed {headline}-day horizon.
          A higher bar for higher-conviction groups means the conviction score is doing its job.
        </p>

        {shownClass !== "all" && (
          <p className="text-muted mb-2" style={{ fontSize: 12.5 }}>
            <strong style={{ textTransform: "capitalize" }}>{shownClass}</strong>-only.{" "}
            {shownClass === "equity"
              ? "Excludes trending FX/crypto/futures whose long-window analogs agree with high conviction on a secular trend — the honest read of the ramp."
              : "These carry secular trends, so a rising ramp here is momentum, not necessarily shape edge."}
          </p>
        )}

        {rows.length === 0 && (
          <p className="text-muted text-center py-4 mb-0">
            No {shownClass} signals for this window.
          </p>
        )}

        <div style={{ position: "relative", height: 200, width: "100%", display: rows.length === 0 ? "none" : "block" }}>
          <Bar data={chartData} options={chartOptions as never} plugins={[baselinePlugin]} />
        </div>
        {rows.length > 0 && (
          <div className="text-muted text-center mt-1" style={{ fontSize: 11 }}>
          Conviction (% of matched analogs that moved in the called direction) · dashed line = 50% coin-flip
          </div>
        )}

        {rows.length > 0 && (
          <Table responsive borderless size="sm" className="mt-3 mb-1 tr-compact-table" style={{ fontSize: 13 }}>
            <thead>
              <tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border-color)" }}>
                <th>Conviction</th>
                <th className="text-end">Signals</th>
                <th className="text-end">Win @ {headline}d</th>
                <th className="text-end">Avg return</th>
                <th className="text-end">Best horizon</th>
                <th className="text-end">Best win rate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ bin, hl }) => (
                <tr key={bin.label}>
                  <td>{bin.label}%</td>
                  <td className="text-end">{bin.n_signals.toLocaleString()}</td>
                  <td className="text-end" style={{ color: hl ? winRateColor(hl.win_rate, 1) : "var(--text-muted)" }}>
                    {hl ? `${hl.win_rate.toFixed(1)}%` : "—"}
                  </td>
                  <td className="text-end">{hl ? `${hl.avg_return >= 0 ? "+" : ""}${hl.avg_return.toFixed(2)}%` : "—"}</td>
                  <td className="text-end">{bin.best_edge ? `${bin.best_edge.horizon}d` : "—"}</td>
                  <td className="text-end">{bin.best_edge ? `${bin.best_edge.win_rate.toFixed(1)}%` : <span className="text-muted">n&lt;{win.min_sample_size}</span>}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <p className="text-muted mt-2 mb-0" style={{ fontSize: 11.5 }}>
          Realized forward returns, signed so a correct bullish/bearish call counts as a win. Best-edge horizon
          is the peak-win-rate horizon among those with ≥ {win.min_sample_size} samples. Signals issued on the
          same day are correlated, so sample counts overstate independent observations — read the trend across
          conviction levels, not small differences. Past performance does not guarantee future results.
        </p>
      </CardBody>
    </Card>
  );
}
